import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '@utils/axiosInstance';
import webvtt from "node-webvtt";

import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { toast, Flip } from 'react-toastify';

import LocalizedPlayer from '../common/LocalizedPlayer';
import SubList from './SubList';
import WaveSurferTimeline from './WaveSurferTimeline';

import {
    setSubtitleList, setChangedSubtitle, setSelectedSubtitle, setChangedTimelineSubtitle,
    addSubtitle
} from '@modules/subtitle';
import {
    setVodInfo, setPlayTime, setSelectedPlayTime
} from '@modules/media';

interface SubEditorProps {
    setNoData: (value: boolean) => void;
}

function SubEditor({ setNoData }: SubEditorProps) {

    // 렌더링 이유 추적
    const renderCountRef = useRef(0);
    renderCountRef.current += 1;

    const dispatch = useDispatch();
    const { uuid } = useParams();
    const location = useLocation();
    const subtitleListRef = useRef(null); // 자막 목록 컨테이너에 대한 ref 추가
    const { t } = useTranslation();

    // URL에서 lang 파라미터 추출
    const getLangFromPath = () => {
        const pathSegments = location.pathname.split('/');
        const subtitleIndex = pathSegments.findIndex((segment: string) => segment === 'subtitle');
        return (subtitleIndex !== -1 && pathSegments.length > subtitleIndex + 1)
            ? pathSegments[subtitleIndex + 1]
            : null;
    };

    const lang = getLangFromPath();

    const subtitleList = useSelector((state: any) => state.subtitle.subtitleList);
    const userToken = useSelector((state: any) => state.organization.userToken);
    const organizationData = useSelector((state: any) => state.organization.organizationData);

    const [isFetched, setIsFetched] = useState(false);
    // const [zoomLevel, setZoomLevel] = useState(50);
    const [zoomLevel, setZoomLevel] = useState(100); // Default zoom
    const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null); // 새로 추가된 자막의 인덱스
    const [videoDurationTime, setVideoDurationTime] = useState(0);

    // 초기화
    useEffect(() => {
        dispatch(setPlayTime(0));
        dispatch(setSelectedPlayTime(0));
        dispatch(setChangedSubtitle({}));
        dispatch(setChangedTimelineSubtitle({}));
        dispatch(setSubtitleList([]));
        dispatch(setSelectedSubtitle({}));
    }, [dispatch]);

    // 자막 가져오기
    useEffect(() => {
        console.log('Fetching subtitle info...', lang, uuid);
        if (lang && uuid) {
            console.log('Fetching subtitle info...');
            fetchSubtitleInfo();
        } else {
            console.log('Missing lang or uuid:', { lang, uuid });
        }
    }, [lang, uuid, userToken, organizationData, location.pathname]);

    const fetchSubtitleInfo = () => {
        console.log('fetchSubtitleInfo called with:', { lang, uuid, userToken, organizationData });
        setIsFetched(false);
        const data = {
            tk: userToken,
            uuid,
            ogc: organizationData.organization_code,
            lang
        };

        console.log('API request data:', data);

        api({
            method: 'post',
            url: '/subtitle/getSubtitle',
            data: data
        })
            .then((response) => {
                console.log('API response:', response.data);

                if (response.data.code === 202) {
                    console.log('Subtitle generation in progress');
                    toast.error(`${lang} 자막을 생성 중입니다. 잠시 후 ${lang} 언어로 다시 이동해주세요.`, {
                        position: "top-center",
                        transition: Flip
                    });
                    setNoData(true);
                    return;
                }

                const webvttString = response.data.data;
                console.log('WebVTT string length:', webvttString?.length);

                if (webvttString?.length > 0) {
                    const parsed = webvtt.parse(webvttString);
                    const cues = parsed.cues || [];
                    console.log('Parsed cues count:', cues.length);

                    if (cues.length > 0) {
                        const mapped = cues.map((cue: any) => ({
                            id: Number(cue.identifier),
                            start: cue.start,
                            end: cue.end,
                            content: cue.text
                        }));
                        console.log('Mapped subtitles:', mapped);
                        dispatch(setSubtitleList(mapped));
                    } else {
                        console.log('No cues found, setting empty subtitle');
                        dispatch(setSubtitleList([{ id: 0, start: 0, end: 1, content: "" }]));
                    }
                    console.log('Setting isFetched to true');
                    setIsFetched(true);
                } else {
                    console.log('No webvtt string, setting empty subtitle');
                    dispatch(setSubtitleList([{ id: 0, start: 0, end: 1, content: "" }]));
                }

                setNoData(false);
            })
            .catch(error => {
                console.error('Error fetching subtitle:', error);
            })
            .finally(() => {
                console.log('Setting isFetched to true');
            });
    };
    // 에러가 나면 다른 언어로 갔다가 noSub으로 돌아가게

    useEffect(() => {
        console.log('isFetched', isFetched);
    }, [isFetched]);

    // 자막 목록이 변경되고 lastAddedIndex가 있을 때 스크롤 처리
    useEffect(() => {
        if (lastAddedIndex !== null && subtitleListRef.current) {
            const subtitleElements = (subtitleListRef.current as HTMLElement).querySelectorAll('.subList');
            if (subtitleElements && subtitleElements[lastAddedIndex]) {
                subtitleElements[lastAddedIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                setLastAddedIndex(null);
            }
        }
    }, [subtitleList, lastAddedIndex]);

    const addSubtitleBlock = () => {
        const idx = subtitleList.length - 1;
        const maxId = Math.max(...subtitleList.map((el: any) => Number(el.id)));
        const prevEnd = subtitleList[idx].end + 2;
        const newEnd = Math.min(prevEnd + 1, videoDurationTime);

        const newElement = {
            id: maxId + 1,
            start: prevEnd,
            end: newEnd,
            content: ""
        };

        // Redux 액션 디스패치
        dispatch(addSubtitle(idx, newElement));

        // API 호출로 서버에 저장
        const newSubtitles = [
            ...subtitleList.slice(0, idx + 1),
            newElement,
            ...subtitleList.slice(idx + 1)
        ];

        saveSubtitlesToServer(newSubtitles);

        // 추가된 자막의 인덱스 저장 (idx + 1이 새로 추가된 자막의 위치)
        setLastAddedIndex(idx + 1);
    };

    const saveSubtitlesToServer = (subArr: any[]) => {
        const formatted = subArr.map(list => ({
            identifier: String(list.id + 1),
            start: list.start,
            end: list.end,
            text: list.content,
            styles: ""
        }));

        const data = {
            tk: userToken,
            uuid,
            ogc: organizationData.organization_code,
            lang,
            subtitle: formatted
        };

        api({
            method: 'post',
            url: '/subtitle/aUpdate',
            data
        })
            .then(res => {
                console.log('edit data', res.data?.data);
            })
            .catch(error => {
                console.error('Error updating subtitle:', error);
            });
    };

    // const handleZoomChange = (e, value) => setZoomLevel(value);
    const handleZoomChange = (event: Event, value: number | number[], activeThumb: number) => {
        setZoomLevel(typeof value === 'number' ? value : value[0]);
    };
    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 20, 500));
    };
    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 20, 20));
    };

    return (
        <div className='container-subtitle-editor subtitle-editor'>
            <div className="wrapper-subtitle-editor">
                <div className='content-subtitle-editor'>
                    <div className="subtitle-editor-layout">
                        <div className="col-subtitle">
                            <div className='box-add-subtitle'>
                                <IconButton className='btn-add-subtitle' onClick={() => addSubtitleBlock()}>
                                    <AddIcon fontSize="small" />
                                    <Typography variant="body2">{t('vod.manage.subtitle')}</Typography>
                                </IconButton>
                            </div>

                            <div className='lists-subtitle' ref={subtitleListRef}>
                                {isFetched ? (
                                    subtitleList.map((el: any, idx: number) => (
                                        <SubList key={`subtitle-${el.start}-${el.end}`} idx={idx} item={el} videoDurationTime={videoDurationTime} />
                                    ))
                                ) : (
                                    <SkeletonSubtitlePlaceholder />
                                )}
                            </div>
                        </div>

                        <div className="col-player">
                            <div className="player-wrapper">
                                {!isFetched ? (
                                    <Skeleton variant="rectangular" width="100%" height="50%" className="skeleton-player" />
                                ) : (
                                    <LocalizedPlayer setVideoDurationTime={setVideoDurationTime} lang={lang} type={'subtitle'} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {isFetched && videoDurationTime > 0 ? (
                    <div className="footer-subtitle-editor">
                        <div className="timeline-container">
                            <div className="timeline-layout">
                                <div className="col-play-time">
                                    {/* <Typography variant="body2">{milliSecondsToFormattedTime(playTime)}</Typography> */}
                                </div>
                                <div className="spacer" />
                                <div className="col-zoom-slider">
                                    <Box className='box-zoom-slider'>
                                        <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 20}>
                                            <ZoomOutIcon fontSize="small" />
                                        </IconButton>
                                        <Slider
                                            value={zoomLevel}
                                            min={20}
                                            max={500}
                                            step={1}
                                            className="editor-tab__zoom-slider"
                                            onChange={handleZoomChange}
                                        />
                                        <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 500}>
                                            <ZoomInIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </div>
                            </div>
                        </div>

                        <div className="timeline-wrapper">
                            <WaveSurferTimeline zoomLevel={zoomLevel} videoDurationTime={videoDurationTime} />
                        </div>
                    </div>
                )
                    :
                    <Box className="timeline-skeleton">
                        <div className="timeline-skeleton-header">
                            <Skeleton variant="rectangular" width={150} height={10} />
                            <Skeleton variant="rectangular" width={100} height={10} />
                        </div>

                        <div className="timeline-skeleton-content">
                            {[...Array(80)].map((_, i) => (
                                <Skeleton key={i} variant="rectangular" width="100%" height={20 + Math.random() * 20} className="timeline-skeleton-bar" />
                            ))}
                        </div>

                        <Skeleton variant="rectangular" height={20} width="100%" className="timeline-skeleton-footer" />
                    </Box>
                }
            </div>
        </div>
    );
}

const SkeletonSubtitlePlaceholder = () => (
    <div className='sub-list'>
        <div className="col-text-subtitle" style={{ border: 'none' }}>
            <Skeleton variant="rounded" width="100%" height="100%" />
        </div>
        <div className="btns-subtitle"><Box className="btn-del" /></div>
        <div className="col-time-subtitle" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
            <Skeleton variant="rounded" width="100%" height="35%" />
            <Skeleton variant="rounded" width="100%" height="35%" />
        </div>
    </div>
);

export default SubEditor;